import { getEnvVar } from 'helpers';
import { Base64 } from 'js-base64';
import DOMPurify from 'dompurify';

var randomBytes = require('randombytes');
export function getRandomDecimal() {
  const randomBytesValue = randomBytes(6);
  const randomValue = randomBytesValue.readUIntLE(0, 6) / 2 ** 48;
  return randomValue;
}
function setDefaultValue(productInfo) {
  return productInfo ? productInfo : [''];
}

export const getParams = (requestParams, productName, productCategory) => {
  for (let param in requestParams) {
    const paramValue = requestParams[param];
    if (Array.isArray(paramValue) && paramValue.length == 0) {
      switch (param) {
        case 'productName':
          requestParams[param] = setDefaultValue(productName);
          break;
        case 'productNames':
          requestParams[param] = setDefaultValue(productName);
          break;
        case 'productCategory':
          requestParams[param] = setDefaultValue(productCategory);
          break;
        default:
          delete requestParams[param];
      }
    }
    if (param == 'promotional') {
      if (paramValue.length == 1) {
        if (paramValue.includes('Promotional')) {
          requestParams[param] = true;
        } else {
          requestParams[param] = false;
        }
      } else if (paramValue.length > 1) {
        delete requestParams[param];
      }
    }
    if (param == 'name' && paramValue.length == 0) {
      delete requestParams[param];
    }
  }
  return requestParams;
};

export const allowedIFrames = () => {
  const targetOrigins = (getEnvVar('ADM_IFRAME_ALLOWED_DOMAINS') || '').split(',') || [];
  let allowedTargets = targetOrigins.filter(targetOrigin => targetOrigin === window.localStorage.getItem('GCM-parent-frame'));
  return allowedTargets;
};

export const isReadOnlyMode = () => {
  return (window.localStorage.getItem('GCM-readOnlyMode') === 'true');
};

export const postMessage = (templateResponse) => {
  if (window.self !== window.top) {
    allowedIFrames().forEach(targetOrigin => {
      window.parent.postMessage(templateResponse, targetOrigin);
    });
  }
}

export const isIFrame = () => {
  return window.self !== window.top;
};

const decodeEntityToken = (entity, options = { decodeTagEntities: true }) => {
  if (!entity || entity[0] !== '&' || entity[entity.length - 1] !== ';') {
    return entity;
  }

  const token = entity.slice(1, -1);
  const namedEntities = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: '\u00A0'
  };

  if (!options.decodeTagEntities && (token === 'lt' || token === 'gt')) {
    return entity;
  }

  if (Object.prototype.hasOwnProperty.call(namedEntities, token)) {
    return namedEntities[token];
  }

  if (token[0] === '#') {
    const isHex = token[1]?.toLowerCase() === 'x';
    const rawValue = isHex ? token.slice(2) : token.slice(1);
    const radix = isHex ? 16 : 10;
    const codePoint = Number.parseInt(rawValue, radix);

    if (Number.isInteger(codePoint) && codePoint >= 0 && codePoint <= 0x10FFFF) {
      try {
        return String.fromCodePoint(codePoint);
      } catch (error) {
        return entity;
      }
    }
  }

  return entity;
};

const decodeEntitiesInString = (value, options = { decodeTagEntities: true }) => {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }

  return value.replace(
    /&(?:#\d+|#x[\da-fA-F]+|[a-zA-Z][\w-]*);/g,
    match => decodeEntityToken(match, options)
  );
};

// Helper to decode HTML entities with enhanced security controls
const decodeHTMLEntities = (str, options = { decodeTagEntities: true }) => {
  if (typeof str !== 'string') return str;
  
  // Security limits
  const maxAttempts = 10; // Reduced for security
  const startTime = Date.now();
  const maxProcessingTime = 1000; // 1 second limit
  
  let decoded = str;
  let previousDecoded = '';
  let attempts = 0;
  
  while (decoded !== previousDecoded && attempts < maxAttempts) {
    // Check processing time limit
    if (Date.now() - startTime > maxProcessingTime) {
      console.warn('HTML entity decoding timeout, potential DoS attack - clearing string');
      return '';
    }
    
    previousDecoded = decoded;
    
    try {
      decoded = decodeEntitiesInString(decoded, options);
    } catch (error) {
      console.warn('Error during HTML entity decoding:', error);
      break;
    }
    
    attempts++;
    
    // Additional safety: Check if string is growing unexpectedly
    if (decoded.length > previousDecoded.length * 2) {
      console.warn('Suspicious string expansion during decoding');
      decoded = previousDecoded; // Revert to previous safe state
      break;
    }
  }
  
  // Clear string if maxAttempts exceeded
  if (attempts >= maxAttempts) {
    console.warn(`Potential encoding attack detected: ${attempts} decoding attempts reached limit - clearing string`);
    return '';
  }
  
  return decoded;
}

export const sanitizeHtml = (html) => {
  if (typeof html !== "string" || (typeof html === "string" && html.trim() === "")) {
    return html;
  }

  const decoded = decodeHTMLEntities(html);

  // Use DOMPurify with security-focused configuration
  const sanitized = DOMPurify.sanitize(decoded, {
    // Use HTML profile only for maximum security
    USE_PROFILES: { html: true },
    
    // Enable DOM Clobbering protection (default is true, but being explicit)
    SANITIZE_DOM: true,
    
    // Disable DOM Clobbering protection to retain original IDs for custom components
    SANITIZE_NAMED_PROPS: false,
    
    // Allow data attributes to retain template and HTML comments necessary for custom components
    ALLOW_DATA_ATTR: true,
    
    // Disable ARIA attributes for security (default is true, but more secure to disable) 
    ALLOW_ARIA_ATTR: false,
    
    // Keep content when removing elements (safer default)
    KEEP_CONTENT: true,
    
    // Force body to prevent edge cases
    FORCE_BODY: true,
    
    // Safe for XML to handle risky HTML characters properly
    SAFE_FOR_XML: true,
    
    // Return as trusted type if available
    RETURN_TRUSTED_TYPE: true,

    // Preserve link behavior metadata needed by print HTML/PDF flows.
    ADD_ATTR: ['target', 'rel', 'href']
  });

  // Ensure we always return a string for compatibility
  return typeof sanitized === 'string' ? sanitized : sanitized.toString();
};

export const sanitizeData = (data, isJsonValue = false) => {
  let sanitizedData;
  if (Array.isArray(data)) {
    sanitizedData = data.map(val => 
      typeof val == 'object' ? sanitizeData(val, isJsonValue) : 
      isJsonValue ? sanitizeJsonString(val) : sanitizeHtml(val)
    );
  } else if (typeof data == 'object' && data !== null) {
    sanitizedData = {};
    for (let idx in data) {
      let key = sanitizeJsonString(idx); // Keys should never have HTML
      let val = typeof data[idx] == 'object' ? 
        sanitizeData(data[idx], isJsonValue) : 
        isJsonValue ? sanitizeJsonString(data[idx]) : sanitizeHtml(data[idx]);
      sanitizedData[key] = val;
    }
  } else {
    sanitizedData = isJsonValue ? sanitizeJsonString(data) : sanitizeHtml(data);
  }
  return sanitizedData;
};

// Add new function to sanitize JSON-safe strings
export const sanitizeJsonString = (str) => {
  
  if (typeof str !== "string" || (typeof str === "string" && str.trim() === "")) {
    return str;
  }
  
  // Only decode entities, don't run through DOMPurify
  // which can break JSON structure
  // For JSON-safe values, avoid decoding < and > entities to prevent
  // accidental HTML token introduction during later JSON parsing.
  // Still perform bounded multi-pass decoding to handle nested encodings.
  return decodeHTMLEntities(str, { decodeTagEntities: false });
};

// Helper function to sanitize HTML while preserving specific comments
export const sanitizeHtmlWithCommentPreservation = (html) => {
  if (typeof html !== "string" || html.trim() === "") {
    return html;
  }

  // Extract all GrapesJS component data comments 
  // Matches patterns like {{!--htmlField...--}}, {{!--dataFrameField...--}}, etc.
  const commentRegex = /{{!--(?:htmlField|dataFrameField|barcodeField|iterationField)[^]*?--}}/g;
  const preservedComments = [];
  let commentIndex = 0;

  // Replace comments with unique placeholders and store them
  let contentWithPlaceholders = html.replace(commentRegex, (match) => {
    const placeholder = `__PRESERVED_COMMENT_${commentIndex}__`;
    preservedComments[commentIndex] = match;
    commentIndex++;
    return placeholder;
  });

  // Sanitize the content with placeholders (original comments are safely stored)
  const sanitized = sanitizeHtml(contentWithPlaceholders);

  // Restore the original comments by replacing placeholders
  let finalContent = sanitized;
  preservedComments.forEach((comment, index) => {
    const placeholder = `__PRESERVED_COMMENT_${index}__`;
    finalContent = finalContent.replace(placeholder, comment);
  });

  return finalContent;
};

export const sanitizeHtmlWithHandlebarPreservation = (html) => {
  if (typeof html !== "string" || html.trim() === "") {
    return html;
  }

  // Step 1: Extract html, head and body tags with their attributes
  const htmlTagMatch = html.match(/(<html[^>]*>)([\s\S]*)(<\/html>)/i);
  const headMatch = html.match(/(<head[^>]*>)([\s\S]*?)(<\/head>)/i);
  const bodyMatch = html.match(/(<body[^>]*>)([\s\S]*?)(<\/body>)/i);
  
  let htmlOpenTag = '';
  let htmlCloseTag = '';
  let headOpenTag = '';
  let headContent = '';
  let headCloseTag = '';
  let bodyOpenTag = '';
  let bodyContent = '';
  let bodyCloseTag = '';
  
  // Preserve HTML wrapper tag
  if (htmlTagMatch) {
    htmlOpenTag = htmlTagMatch[1];
    htmlCloseTag = htmlTagMatch[3];
  }
  
  if (headMatch) {
    headOpenTag = headMatch[1];
    headContent = headMatch[2];
    headCloseTag = headMatch[3];
  }
  
  if (bodyMatch) {
    bodyOpenTag = bodyMatch[1];
    bodyContent = bodyMatch[2];
    bodyCloseTag = bodyMatch[3];
  } else {
    // If no body tags, treat entire HTML as body content
    bodyContent = html;
  }

  // Step 2: Preserve ALL Handlebars by wrapping them in special HTML comments
  // HTML comments are preserved by DOMPurify and don't affect structure
  const preserveHandlebars = (content) => {
    const handlebarMap = [];
    let index = 0;
    
    // First, preserve Handlebars in attributes by replacing entire attributes
    // Pattern matches attributes like: src="{{barCode}}" or href="{{link}}"
    const withPreservedAttributes = content.replace(/(\s+[\w-]+\s*=\s*["'])(\{\{[^}]+\}\}|[^"']*\{\{[^}]+\}\}[^"']*)(['"])/g, (match, prefix, value, suffix) => {
      if (value.includes('{{')) {
        const placeholder = `HBSPRESERVE${index}`;
        handlebarMap.push({ placeholder, original: value });
        index++;
        // Replace the attribute value with a safe placeholder
        return `${prefix}PLACEHOLDER_${placeholder}${suffix}`;
      }
      return match;
    });
    
    // Then, replace all remaining Handlebar expressions with HTML comments
    const withPlaceholders = withPreservedAttributes.replace(/(\{\{\{[^}]+\}\}\}|\{\{[^}]+\}\})/g, (match) => {
      const placeholder = `HBSPRESERVE${index}`;
      handlebarMap.push({ placeholder, original: match });
      index++;
      // Wrap in HTML comment so DOMPurify preserves it
      return `<!--${placeholder}-->`;
    });
    
    return { content: withPlaceholders, map: handlebarMap };
  };

  // Step 3: Sanitize with DOMPurify (HTML comments must be preserved)
  const sanitizeContent = (content) => {
    const decoded = decodeHTMLEntities(content);
    
    const sanitized = DOMPurify.sanitize(decoded, {
      USE_PROFILES: { html: true },
      SANITIZE_DOM: true,
      SANITIZE_NAMED_PROPS: false,
      ALLOW_DATA_ATTR: true,
      ALLOW_ARIA_ATTR: false,
      KEEP_CONTENT: true,
      FORCE_BODY: false,
      RETURN_TRUSTED_TYPE: false,
      WHOLE_DOCUMENT: false,
      // CRITICAL: Add HTML comments and head tags to allowed tags
      ADD_TAGS: ['#comment', 'meta', 'link', 'style', 'title'],  // Preserve HTML comments and head elements
      ADD_ATTR: ['charset', 'content', 'http-equiv', 'name', 'property', 'rel', 'href', 'target', 'media', 'type']  // Preserve meta/link attributes
    });
    
    return typeof sanitized === 'string' ? sanitized : sanitized.toString();
  };

  // Step 4: Restore Handlebars from HTML comments and attribute placeholders
  const restoreHandlebars = (content, handlebarMap) => {
    let restored = content;
    handlebarMap.forEach(({ placeholder, original }) => {
      // Replace both HTML comments and attribute placeholders with the original Handlebar
      const commentPattern = `<!--${placeholder}-->`;
      const attrPattern = `PLACEHOLDER_${placeholder}`;
      restored = restored.split(commentPattern).join(original);
      restored = restored.split(attrPattern).join(original);
    });
    return restored;
  };

  // Step 5: Apply preservation and sanitization
  // Note: Head content is preserved as-is since it contains meta tags, styles, etc.
  // that don't need sanitization and DOMPurify strips them when not in proper document context
  const headPreserved = headContent ? preserveHandlebars(headContent) : { content: '', map: [] };
  const bodyPreserved = preserveHandlebars(bodyContent);

  // Only sanitize body content - head content is typically safe (meta tags, styles, etc.)
  const sanitizedBodyContent = sanitizeContent(bodyPreserved.content);

  // Step 6: Restore the Handlebars
  const finalHeadContent = headContent ? restoreHandlebars(headPreserved.content, headPreserved.map) : '';
  const finalBodyContent = restoreHandlebars(sanitizedBodyContent, bodyPreserved.map);

  // Step 7: Reconstruct the HTML with original tag attributes, including the <html> wrapper
  let result = '';
  
  // Start with HTML tag if it exists
  if (htmlOpenTag) {
    result = htmlOpenTag;
  }
  
  if (headMatch) {
    result += `${headOpenTag}${finalHeadContent}${headCloseTag}`;
  }
  
  if (bodyMatch) {
    result += `${bodyOpenTag}${finalBodyContent}${bodyCloseTag}`;
  } else {
    result += finalBodyContent;
  }
  
  // Close HTML tag if it exists
  if (htmlCloseTag) {
    result += htmlCloseTag;
  }

  return result;
};

export const exportGrapeJsContent = (currentHtml, currentCss) => {
  let obj = {};
  obj.css = currentCss;

  let content = currentHtml;
  let startingBodyTag = content.match(/<body[^>]*>/i);
  let elem = document.createElement('div');
  let replaceMap = [];

  elem.innerHTML = content;
  elem.querySelectorAll('.jsx-syntax').forEach(tag => {
    let search = tag.outerHTML;
    let replaceText = tag.innerText;
    replaceMap.push({ search, replaceText });
  });
  elem.querySelectorAll('.highlight').forEach(tag => {
    let search = tag.outerHTML;
    tag.setAttribute('style', '{{highlightStyle}}');
    let replaceText = tag.outerHTML;
    replaceMap.push({ search, replaceText });
  });
  elem.querySelectorAll('.subscript').forEach(tag => {
    let search = tag.outerHTML;
    tag.setAttribute('style', '{{../this.subscriptStyle}}');
    let replaceText = tag.outerHTML;
    replaceMap.push({ search, replaceText });
  });

  replaceMap.forEach(map => {
    content = content.replace(map.search, map.replaceText);
  });

  const convertImageActionsToLinks = html => {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    wrapper.querySelectorAll('img[data-image-action-url]').forEach(image => {
      const actionUrl = (image.getAttribute('data-image-action-url') || '').trim();
      const actionTarget = (image.getAttribute('data-image-action-target') || '_self').trim();

      const parentAnchor = image.parentElement;
      if (!actionUrl) {
        if (parentAnchor && parentAnchor.tagName === 'A') {
          parentAnchor.replaceWith(image);
        }
        return;
      }

      if (parentAnchor && parentAnchor.tagName === 'A') {
        parentAnchor.setAttribute('href', actionUrl);
        if (actionTarget === '_blank') {
          parentAnchor.setAttribute('target', '_blank');
          parentAnchor.setAttribute('rel', 'noopener noreferrer');
        } else {
          parentAnchor.setAttribute('target', '_self');
          parentAnchor.removeAttribute('rel');
        }
        return;
      }

      const link = document.createElement('a');
      link.setAttribute('href', actionUrl);
      link.setAttribute('style', 'cursor: pointer;');
      
      if (actionTarget === '_blank') {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      } else {
        link.setAttribute('target', '_self');
      }

      const parent = image.parentNode;
      if (parent) {
        parent.replaceChild(link, image);
      }
      link.appendChild(image);
    });

    return wrapper.innerHTML;
  };

  // Check if sanitization is enabled via environment config
  const isSanitizationEnabled = getEnvVar('ADM_ENABLE_HTML_SANITIZATION') === 'true';
  
  // Sanitize content while preserving component data comments (only if enabled)
  const processedContent = isSanitizationEnabled 
    ? sanitizeHtmlWithCommentPreservation(convertImageActionsToLinks(content))
    : convertImageActionsToLinks(content);

  obj.html = `<html><head><style>${obj.css}</style></head>${
    startingBodyTag[0] ? startingBodyTag[0] : `<body>`
  }${processedContent}</body></html>`;
  return obj;
};

export const setGrapesJsBodyId = (editor, html) => {
  if(html) {
    const regex = /<body\s+[^>]*id=["']([^"']+)["']/i;
    const match = html.match(regex);
    if (match && match.length > 1) {
      editor.getWrapper().setId(match[1]);
    }
  }
};

export const removeMetaTagsFromContent = content => {
  if (!content || typeof content !== 'string') return content;
  let metaTagRegex = /<\s*meta(?:\s+[\w-]+=(?:"[^"]*"|'[^']*'))*\s*\/?>/g;
  return content.replace(metaTagRegex, '');
};

export const safeBase64Encode = (content, shouldRemoveMetaTags = false, shouldStringify = true) => {
  try {
    if (content === null || content === undefined) return null;
    
    let stringContent;
    if (shouldStringify) {
      stringContent = JSON.stringify(content);
    } else {
      stringContent = typeof content === 'string' ? content : JSON.stringify(content);
    }
    
    if (shouldRemoveMetaTags) {
      stringContent = removeMetaTagsFromContent(stringContent);
    }
    
    return Base64.encode(stringContent);
  } catch (error) {
    console.error('Encoding failed:', error);
    return null;
  }
};

export const safeBase64Decode = (content, shouldParse = true) => {
  try {
    if (!content) return undefined;
    const decoded = Base64.decode(content);
    return shouldParse ? JSON.parse(decoded) : decoded;
  } catch (error) {
    console.error('Decoding failed:', error);
    return undefined;
  }
};

export const encodeTemplateContent = template => {
  function isBase64(str) {
    try {
      return btoa(atob(str)) == str;
    } catch (err) {
      return false;
    }
  }
  if (!isBase64(template.jsonContent)) {
    if (template.content) {
      template.content = safeBase64Encode(template.content, true);
    } else {
      template.content = safeBase64Encode(template.jsonContent.html, true);
    }
    template.jsonContent = safeBase64Encode(template.jsonContent);
  }
  return template;
};

export const isArrayValuesEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();
  return sortedArr1.every((value, index) => value === sortedArr2[index]);
};

export const isArrayLengthEqual = (component1, component2, component3) => {
  return (
    (component1.length === component2.length) && (component2.length === component3.length)
  );
}